import { memo } from "react";

import {
  type Maybe,
  type MoneyV2,
} from "@shopify/hydrogen-react/storefront-api-types";

import { AddToCartButton } from "@components/Button/AddToCartButton.data";
import { Heading } from "@components/Heading";
import { Price } from "@components/Price";
import {
  type OPProduct,
  type OPProductVariant,
  type ThemeMode,
} from "@root/tablescape/state";
import { type TTailwindString, tw } from "classnames";

type Props = {
  addToCartButtonInView: boolean;
  bundleValueText: string;
  compareAtPrice?: Maybe<MoneyV2>;
  desktopEnabled: boolean;
  formatBtnText: {
    available: string;
    soldOut: string;
    waitlisted: string;
  };
  intelligemsEnabled: boolean;
  mobileEnabled: boolean;
  price: MoneyV2;
  product: OPProduct;
  selectedVariant: OPProductVariant;
  theme: ThemeMode;
  title: string;
};

const styles = {
  base: ({
    addToCartButtonInView,
    desktopEnabled,
    mobileEnabled,
    theme,
  }: Pick<
    Props,
    "addToCartButtonInView" | "desktopEnabled" | "mobileEnabled" | "theme"
  >) =>
    tw(
      // Shared Styles
      "z-1000",
      "box-border",
      "w-full",
      "leading-0",
      "transition-[transform,opacity]" as TTailwindString,
      "duration-[150ms,0ms]" as TTailwindString,
      "bg-background-primary",
      "border-0",
      "border-solid",
      "border-divider",

      // Visibility Styles
      {
        hidden: !mobileEnabled,
        "mg:hidden": !desktopEnabled,
        "opacity-0": addToCartButtonInView,
        ["delay-[0ms,150ms]" as TTailwindString]: addToCartButtonInView,
        "opacity-100": !addToCartButtonInView,
      },

      // Desktop Styles
      {
        [tw(
          "mg:block",
          "mg:fixed",
          "mg:top-16",
          "mg:bottom-auto",
          {
            "mg:-translate-y-full": addToCartButtonInView,
            "mg:translate-y-0": !addToCartButtonInView,
          },
          {
            "mg:shadow-dark-product-bar":
              !addToCartButtonInView && theme === "dark",
            "mg:shadow-light-product-bar":
              !addToCartButtonInView && theme === "light",
            "mg:shadow-none": addToCartButtonInView,
          },
          "mg:border-b",
          "mg:px-12"
        )]: desktopEnabled,
      },

      // Mobile Styles
      {
        [tw("fixed", "bottom-0", {
          "translate-y-full": addToCartButtonInView,
          "translate-y-0": !addToCartButtonInView,
        })]: mobileEnabled,
      }
    ),
  headingWrapper: tw(
    "mx-auto",
    "flex",
    "h-12",
    "flex-row",
    "items-center",
    "gap-8",
    "mg:h-16",
    "text-primary"
  ),
  headingInner: tw("mt-1", "hidden", "mg:flex", "mg:flex-1"),
  priceWrapper: tw("hidden", "items-baseline", "mg:flex"),
  priceInner: tw("mt-1", "flex"),
  buttonWrapper: tw(
    "w-full",
    "mg:w-auto",
    "mg:min-w-[184px]" as TTailwindString
  ),
};

const StickyProductBarViewBase = ({
  addToCartButtonInView,
  bundleValueText,
  compareAtPrice,
  desktopEnabled,
  formatBtnText,
  intelligemsEnabled,
  mobileEnabled,
  price,
  product,
  selectedVariant,
  theme,
  title,
}: Props) => {
  return (
    <div
      className={styles.base({
        addToCartButtonInView,
        desktopEnabled,
        mobileEnabled,
        theme,
      })}
      data-anchor-selector="{{ anchor_selector }}"
      data-theme={theme}
    >
      <div className={styles.headingWrapper}>
        <div className={styles.headingInner}>
          <Heading as="h1" size="sm">
            {title}
          </Heading>
        </div>

        <div className={styles.priceWrapper}>
          <div className={styles.priceInner}>
            <Price
              bundleValueText={bundleValueText}
              compareAtPrice={compareAtPrice}
              compareAtPriceDisplay={selectedVariant.compareAtPriceDisplay}
              price={price}
              showCurrencySuffix={false}
            />
          </div>
        </div>

        <div className={styles.buttonWrapper}>
          <AddToCartButton
            formatBtnText={formatBtnText}
            isIntelligemsEnabled={intelligemsEnabled}
            products={[product]}
            rounded={false}
            theme={theme}
            variants={[selectedVariant]}
          />
        </div>
      </div>
    </div>
  );
};

export const StickyProductBarView = memo(StickyProductBarViewBase);
